import React, { useState, useMemo } from "react";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import SingleSelect from "@/components/controls/SingleSelect";
import { UserData } from "@/entities/users/types";
import { useTranslation } from "react-i18next";
import { useRoles } from "@/entities/users/hooks";

const TRANSLATION_KEY = "settings.userManagement.changeRoleModal";

interface RoleOption {
  id: string;
  displayName: string;
  subtitle: string;
}

interface ChangeRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUsers: UserData[];
  onApplyChanges: (newRole: string) => void;
}

const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  isOpen,
  onClose,
  selectedUsers,
  onApplyChanges
}) => {
  const { t } = useTranslation();
  const { data: rolesData } = useRoles();
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");

  const roleOptions = useMemo((): RoleOption[] => {
    if (!rolesData?.roles) return [];

    return rolesData.roles.map((role: any) => ({
      id: role.id,
      displayName: role.name,
      subtitle: role.description
    }));
  }, [rolesData]);

  const handleApplyChanges = () => {
    if (selectedRoleId) {
      onApplyChanges(selectedRoleId);
      onClose();
      setSelectedRoleId("");
    }
  };

  const handleCancel = () => {
    onClose();
    setSelectedRoleId("");
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => !open && onClose()}
      title={<Text variant="title3H1">{t(`${TRANSLATION_KEY}.title`)}</Text>}
      content={
        <div className="flex flex-col gap-6 p-6">
          <Text variant="body">
            {t(`${TRANSLATION_KEY}.description`, {
              count: selectedUsers.length
            })}
          </Text>

          <div className="flex items-center justify-between gap-4">
            <Text variant="body" className="min-w-0">
              {t(`${TRANSLATION_KEY}.selectRole`)}
            </Text>
            <div className="w-[314px]">
              <SingleSelect
                fieldName="select-role"
                options={roleOptions}
                selectedValue={selectedRoleId}
                onSelection={(value) => setSelectedRoleId(value as string)}
                placeholder={t(`${TRANSLATION_KEY}.select`)}
                variant="form"
                showSearch={true}
                className="w-full"
                popoverClassName="w-[314px]"
                showSelectedItemSubtitle={false}
              />
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondaryNoBg" onClick={handleCancel}>
            {t("cancel")}
          </Button>
          <Button onClick={handleApplyChanges} disabled={!selectedRoleId}>
            {t(`${TRANSLATION_KEY}.applyChanges`)}
          </Button>
        </div>
      }
      contentClassname="max-w-2xl"
      hideDivider={true}
    />
  );
};

export default ChangeRoleModal;
